'use client'

import { useEffect, useRef, useState } from 'react'

/**
 * useScrollThreshold
 *
 * Returns true each time the user scrolls >= threshold pixels from the last
 * "checkpoint" position, in any direction. The checkpoint resets after every
 * detected scroll burst, so consecutive scroll events are each detected
 * independently.
 *
 * @param {number} [threshold=100] - Minimum pixels of accumulated scroll
 *                                   distance before the hook fires.
 * @returns {boolean} - true during the frame when the threshold is crossed,
 *                      resets to false immediately after so the next crossing
 *                      can be detected.
 */
export default function useScrollThreshold(threshold = 100) {
  const [triggered, setTriggered] = useState(false)
  const checkpointY = useRef(typeof window !== 'undefined' ? window.scrollY : 0)
  const ticking = useRef(false)

  useEffect(() => {
    const updateScroll = () => {
      const scrollY = window.scrollY || window.pageYOffset
      const delta = Math.abs(scrollY - checkpointY.current)

      if (delta >= threshold) {
        checkpointY.current = scrollY
        setTriggered(true)
      }

      ticking.current = false
    }

    const onScroll = () => {
      if (!ticking.current) {
        window.requestAnimationFrame(updateScroll)
        ticking.current = true
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true })

    return () => {
      window.removeEventListener('scroll', onScroll)
    }
  }, [threshold])

  useEffect(() => {
    if (triggered) {
      setTriggered(false)
    }
  }, [triggered])

  return triggered
}
